import type { ComponentType } from 'react';
import { List, Loader } from '@ksyos/design-system';
import { useLocation, useParams } from '@ksyos/router';
import { useEffect, useState } from 'react';
import type { IEmployment } from '@frontend/components/interfaces';
import { ManagerAuthorization } from '@common/enums';
import { getLanguage } from '@common/translate/translate';
import { isActiveOnDate } from '@common/utils/isActiveOnDate';
import { PageTemplate } from '@frontend/components/templates/PageTemplate';
import { fetchJson } from '@frontend/store/fetch';
import { assertHasAuthorization, hasOneAuthorization } from '@frontend/utils';
import type { IPractitioner } from '../../interfaces/practitioner';
import { DisplayMessage } from '../../elements/DisplayMessage';
import { NullableFormattedDate } from '../../elements/NullableFormattedDate';
import { NullableFormattedTime } from '../../elements/NullableFormattedTime';
import { SideNavigationTemplate } from '../../templates/SideNavigationTemplate';
import { PractitionerEmploymentsList } from './PractitionerEmploymentsList';
import { PractitionerServiceReadersList } from './PractitionerServiceReadersList';
import { PractitionerServicesConsumingList } from './PractitionerServicesConsumingList';
import { PractitionerServicesProvidingList } from './PractitionerServicesProvidingList';
import { PractitionerServiceSupervisorsList } from './PractitionerServiceSupervisorsList';
import { RegistrationCode } from './RegistrationCode';

interface IPractitionerDetailed extends IPractitioner {
    isRegistered: boolean;
    lastLoginOn?: string;
    registrationCode: string | null;
    registrationCodeGeneratedDatetime: string | null;
    registrationDate: string | null;
    uid?: number;
}

interface ITab {
    content: JSX.Element;
    name: string;
    title: string;
}

export type PractitionerDetailsTabProps = {
    practitionerId: string;
};

export function PractitionerDetails() {
    const { employment, practitioner, common, supervisors, readers } = getLanguage();
    const { id: practitionerId } = useParams() as { id: string };
    const { pathname } = useLocation();
    const [isLoading, setIsLoading] = useState(true);
    const [currentPractitioner, setCurrentPractitioner] = useState<IPractitionerDetailed | null>(null);
    const [showDisplayMessage, setShowDisplayMessage] = useState(false);
    const { employmentsOld, serviceRightsCareproviders } = ManagerAuthorization;

    assertHasAuthorization([employmentsOld, serviceRightsCareproviders]);

    useEffect(() => {
        loadData();
    }, []);

    const tabs: ITab[] = [];

    if (hasOneAuthorization(employmentsOld)) {
        tabs.push(
            createTab(employment.employments.replace(' ', ''), employment.employments, PractitionerEmploymentsList)
        );
    }

    if (hasOneAuthorization(serviceRightsCareproviders)) {
        tabs.push(
            createTab(
                'servicesProvidingListIsVisible',
                practitioner.providingServices,
                PractitionerServicesProvidingList
            ),
            createTab(
                'servicesConsumingListIsVisible',
                practitioner.consumingServices,
                PractitionerServicesConsumingList
            ),
            createTab(
                'serviceSupervisorsListIsVisible',
                supervisors.supervisingServices,
                PractitionerServiceSupervisorsList
            ),
            createTab('serviceReadersListIsVisible', readers.readingServices, PractitionerServiceReadersList)
        );
    }

    const { nameFamily, nameGiven, nameInfix, uid, lastLoginOn } = currentPractitioner || {};

    const breadcrumb = [
        { text: practitioner.practitioners, url: '/practitioners' },
        { text: practitioner.details, url: pathname },
    ];

    const infoItems = currentPractitioner
        ? [
              { label: common.nameFamily, value: nameFamily },
              { label: common.nameGiven, value: nameGiven },
              { label: common.nameInfix, value: nameInfix },
              { label: practitioner.uid, value: uid },
              {
                  label: practitioner.lastLogin,
                  value: (
                      <>
                          <NullableFormattedDate value={lastLoginOn} />
                          &nbsp;
                          <NullableFormattedTime value={lastLoginOn} />
                      </>
                  ),
              },
              { label: '', value: <RegistrationCode practitioner={currentPractitioner} /> },
          ]
        : [];

    return (
        <PageTemplate width="full">
            {!isLoading && showDisplayMessage ? (
                <DisplayMessage
                    message={employment.noEmployments}
                    messageType="info"
                />
            ) : null}
            <SideNavigationTemplate
                breadcrumb={breadcrumb}
                infoBlock={
                    currentPractitioner ? (
                        <List>
                            {infoItems.map(({ label, value }, index) => (
                                <List.Item key={index}>
                                    {label && `${label}: `}
                                    {value ?? ''}
                                </List.Item>
                            ))}
                        </List>
                    ) : (
                        <Loader sx={{ height: '3rem' }} />
                    )
                }
                tabs={tabs}
            />
        </PageTemplate>
    );

    async function loadData() {
        const practitionerData = await loadPractitioner();
        if (practitionerData !== null) {
            await loadEmployments(practitionerData.id);
        }
        setIsLoading(false);
    }

    async function loadPractitioner(): Promise<IPractitionerDetailed | null> {
        const url = `/api/practitioners/${practitionerId}`;
        const result = await fetchJson(url, 'GET');
        if (result.json) {
            setCurrentPractitioner(result.json);

            return result.json;
        }

        return null;
    }

    async function loadEmployments(newPractitionerId: string) {
        const url = `/api/employments/${newPractitionerId}/organizations`;
        const result = await fetchJson(url, 'GET');
        if (result.json) {
            setShowDisplayMessage(shouldShowBanner(result.json.items));
        }
    }

    function shouldShowBanner(employments: IEmployment[]) {
        if (employments.length === 0) {
            return true;
        }

        return employments.every((item: IEmployment) => !isActiveOnDate(item, new Date()));
    }

    function createTab(name: string, title: string, Component: ComponentType<PractitionerDetailsTabProps>) {
        return {
            content: <Component practitionerId={practitionerId} />,
            name,
            title,
        };
    }
}
